import * as React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@finster/ui";

import { CitationContent } from "../content/CitationContent";
import type { SourceItem } from "../../ai/sourceItems";
import type { Citation } from "../../types";

interface CitationDrawerProps {
  /** True when a citation is currently being inspected. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Target for the drawer body. Exactly one of these should be set
   * when `open` is true:
   *
   *   - `citationId` — an inline `<u id="…">` uuid (the user clicked
   *     an underline in the chat). The drawer resolves the uuid to
   *     a specific source link/row.
   *   - `sourceItem` — a fully resolved `SourceItem` (the user
   *     clicked "View source" on a Retrieved-sources / Citations
   *     card). The drawer renders the source directly.
   */
  citationId?: string | null;
  sourceItem?: SourceItem | null;
  /** The pool of citations for the active assistant turn. */
  citations: Citation[];
}

/**
 * Right-anchored sheet that renders source information for a clicked
 * citation. Lifted from finster-chat's "Source information" drawer in
 * concept but slimmed to the data the PPT add-in surfaces.
 *
 * Closes on backdrop click or Escape via Radix's `Sheet` semantics.
 */
export const CitationDrawer: React.FC<CitationDrawerProps> = ({
  open,
  onOpenChange,
  citationId,
  sourceItem,
  citations,
}) => {
  const hasTarget = Boolean(citationId) || Boolean(sourceItem);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-[420px] max-w-[95vw] flex-col gap-3 p-4">
        <SheetHeader className="text-left">
          <SheetTitle className="text-[14px] font-semibold">Source information</SheetTitle>
        </SheetHeader>
        {open && hasTarget ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <CitationContent
              citationId={citationId ?? null}
              sourceItem={sourceItem ?? null}
              citations={citations}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
